// File: frontend/src/services/pinService.ts

import apiClient from "./apiClient";
import { PublicPinModel, AdminPinModel, PinTargetType } from "../models/PinModel";

const PUBLIC_URL = "/api/pins";
const ADMIN_URL = "/api/admin/pins";

/**
 * Sitewide pin service.
 *
 * Public part: GET /api/pins is fetched once (module-level cache) and shared by
 * every consumer through a tiny pub/sub store — no context provider needed, so
 * App.tsx stays untouched. Admin mutations call refreshPins() so all subscribed
 * components (left-pane lists, topic pages, admin panel) update immediately.
 */

// ── Tiny pub/sub store ─────────────────────────────────────────────────────

let cachedPins: PublicPinModel[] = [];
let loaded = false;
let inflight: Promise<PublicPinModel[]> | null = null;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((l) => l());

export const pinStore = {
  /** Current snapshot (may be empty before the first fetch resolves). */
  getPins(): PublicPinModel[] {
    return cachedPins;
  },
  /** Subscribe to pin changes; returns the unsubscribe function. */
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  /** Fetch once; concurrent callers share the same request. */
  async ensureLoaded(): Promise<PublicPinModel[]> {
    if (loaded) return cachedPins;
    if (!inflight) {
      inflight = apiClient
        .get<PublicPinModel[]>(PUBLIC_URL)
        .then((pins) => {
          cachedPins = pins || [];
          loaded = true;
          notify();
          return cachedPins;
        })
        .catch((err) => {
          // Pins are a progressive enhancement — never break the page on failure
          console.error("Failed to load pins:", err);
          loaded = true;
          return cachedPins;
        })
        .finally(() => {
          inflight = null;
        });
    }
    return inflight;
  },
  /** Force a refetch — called after every admin pin mutation. */
  async refresh(): Promise<void> {
    try {
      cachedPins = (await apiClient.get<PublicPinModel[]>(PUBLIC_URL)) || [];
      loaded = true;
      notify();
    } catch (err) {
      console.error("Failed to refresh pins:", err);
    }
  },
};

// ── Admin mutations (ADMIN + MODERATOR) ────────────────────────────────────

export const adminPinService = {
  async listPins(): Promise<AdminPinModel[]> {
    return apiClient.get<AdminPinModel[]>(ADMIN_URL);
  },

  async pinTopic(type: PinTargetType, targetId: number): Promise<void> {
    await apiClient.put<void>(`${ADMIN_URL}/${type}/${targetId}/topic`);
    await pinStore.refresh();
  },

  async unpinTopic(type: PinTargetType, targetId: number): Promise<void> {
    await apiClient.delete<void>(`${ADMIN_URL}/${type}/${targetId}/topic`);
    await pinStore.refresh();
  },

  async pinEntry(type: PinTargetType, targetId: number, entryId: number): Promise<void> {
    await apiClient.put<void>(`${ADMIN_URL}/${type}/${targetId}/entry/${entryId}`);
    await pinStore.refresh();
  },

  async unpinEntry(type: PinTargetType, targetId: number): Promise<void> {
    await apiClient.delete<void>(`${ADMIN_URL}/${type}/${targetId}/entry`);
    await pinStore.refresh();
  },
};

export default pinStore;